import React, { useEffect } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import modelImg from "../images/Hyundai-3d-model.png";
import modelAccessoriesImg from "../images/Hyundai-3d-model-with-accessories.png";

const PageContainer = styled.div`
  background: #010606;
  color: #fff;
  min-height: 100vh;
`;

const Hero = styled.div`
  padding: 140px 24px 80px;
  max-width: 1100px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  display: block;
  color: #f5554f;
  text-decoration: none;
  font-size: 14px;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 40px;

  &:hover {
    color: #ea342e;
  }
`;

const Tag = styled.span`
  font-size: 13px;
  color: #f5554f;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  font-weight: 500;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 600;
  margin: 16px 0 24px;
  line-height: 1.1;

  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #ccc;
  max-width: 700px;
`;

const HeroImage = styled.img`
  width: 100%;
  border-radius: 12px;
  margin-top: 60px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const Section = styled.section`
  max-width: 1100px;
  margin: 0 auto;
  padding: 80px 24px;
  border-top: 1px solid #1a1a1a;
`;

const SectionTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 500;
  margin-bottom: 32px;
  color: #f7f8fa;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
`;

const Card = styled.div`
  background: #111;
  border-radius: 10px;
  padding: 28px;
  border: 1px solid #1e1e1e;
`;

const CardIcon = styled.div`
  font-size: 1.8rem;
  margin-bottom: 16px;
`;

const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #f5554f;
  margin-bottom: 10px;
`;

const CardText = styled.p`
  font-size: 14px;
  line-height: 1.7;
  color: #aaa;
`;

const AppRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
  margin-bottom: 80px;

  &:last-child {
    margin-bottom: 0;
  }

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const AppImage = styled.img`
  width: 100%;
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
`;

const AppTextBlock = styled.div``;

const AppName = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  color: #f7f8fa;
  margin-bottom: 12px;
`;

const AppDesc = styled.p`
  font-size: 15px;
  line-height: 1.8;
  color: #aaa;
`;

const AppBtn = styled.a`
  display: inline-block;
  margin-top: 16px;
  padding: 10px 24px;
  background: transparent;
  color: #f5554f;
  border: 1px solid #f5554f;
  border-radius: 8px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: #f5554f;
    color: #fff;
  }
`;

const TechList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  list-style: none;
  padding: 0;
`;

const TechItem = styled.li`
  background: #1a1a1a;
  border: 1px solid #2a2a2a;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  color: #ddd;
`;

const BtnRow = styled.div`
  margin-top: 24px;
`;

const PrimaryBtn = styled.a`
  display: inline-block;
  padding: 12px 28px;
  background: transparent;
  color: #f5554f;
  border: 1px solid #f5554f;
  border-radius: 8px;
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: #f5554f;
    color: #fff;
  }
`;

const HyundaiModel = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageContainer>
      <Navbar />
      <Hero>
        <BackLink to="/">← Back to Portfolio</BackLink>
        <Tag>Professional Work · Hyundai Australia</Tag>
        <Title>Hyundai 3D Model</Title>
        <Subtitle>
          Simplified 3D model rendering across Hyundai accessories and vehicle
          landing pages, cutting development time by 30% and reducing system
          complexity. Replaced hardcoded per-model logic with a scalable,
          JSON-driven architecture supporting multiple car variants.
        </Subtitle>
        <BtnRow>
          <PrimaryBtn
            href="https://www.hyundai.com/au/en/cars/suvs/kona#variants:~:text=Engine%20%26%20Transmission%20Options"
            target="_blank"
            rel="noreferrer"
          >
            Visit Project ↗
          </PrimaryBtn>
        </BtnRow>
        <HeroImage src={modelImg} alt="Hyundai 3D Model" />
      </Hero>

      <Section>
        <SectionTitle>What I Built</SectionTitle>
        <Grid>
          <Card>
            <CardIcon>🔧</CardIcon>
            <CardTitle>JSON-Driven Rendering</CardTitle>
            <CardText>
              Replaced hardcoded per-model 3D logic with a JSON-driven system,
              allowing a single codebase to support multiple Hyundai car landing
              pages and accessories pages across many variants — without any code
              changes per model.
            </CardText>
          </Card>
          <Card>
            <CardIcon>🎨</CardIcon>
            <CardTitle>Admin Editor</CardTitle>
            <CardText>
              Built an admin editor that allows 3D designers to show or hide
              specific 3D objects per variant through a UI, completely removing
              the dependency between developers and designers for 3D
              customisation.
            </CardText>
          </Card>
          <Card>
            <CardIcon>🚗</CardIcon>
            <CardTitle>Multi-Variant Support</CardTitle>
            <CardText>
              The system supports multiple car variants simultaneously, including
              different colours, variant trims, and N Line (sport) options.
              Each variant's 3D configuration is defined in a JSON file, making
              it easy to onboard new models and display the correct vehicle
              per variant without touching application code.
            </CardText>
          </Card>
          <Card>
            <CardIcon>⚡</CardIcon>
            <CardTitle>30% Faster Development</CardTitle>
            <CardText>
              By abstracting the 3D rendering logic into a reusable, config-driven
              system, development time for new vehicle pages was cut by 30% while
              also reducing overall system complexity and maintenance overhead.
            </CardText>
          </Card>
        </Grid>
      </Section>

      <Section>
        <SectionTitle>The Two Pages</SectionTitle>
        <AppRow>
          <AppImage src={modelImg} alt="Hyundai 3D Vehicle Model" />
          <AppTextBlock>
            <Tag>Vehicle Landing Page</Tag>
            <AppName>Hyundai 3D Vehicle Model</AppName>
            <AppDesc>
              Interactive 3D vehicle landing pages for Hyundai car models.
              The JSON-driven system dynamically loads and renders the correct
              3D objects based on the selected variant, replacing the previous
              hardcoded implementation that required developer involvement for
              every new model.
            </AppDesc>
            <AppBtn
              href="https://www.hyundai.com/au/en/cars/suvs/kona#variants:~:text=Engine%20%26%20Transmission%20Options"
              target="_blank"
              rel="noreferrer"
            >
              Visit Site ↗
            </AppBtn>
          </AppTextBlock>
        </AppRow>
        <AppRow>
          <AppTextBlock>
            <Tag>Accessories Page</Tag>
            <AppName>Hyundai 3D Accessories</AppName>
            <AppDesc>
              3D accessories pages allowing customers to visualise add-on
              accessories on their selected vehicle variant. Accessories are
              configured through the admin editor, giving designers full control
              over which accessories appear for each model without requiring code
              changes.
            </AppDesc>
            <AppBtn
              href="https://www.hyundai.com/au/en/owning/accessories/inster"
              target="_blank"
              rel="noreferrer"
            >
              Visit Site ↗
            </AppBtn>
          </AppTextBlock>
          <AppImage src={modelAccessoriesImg} alt="Hyundai 3D Accessories" />
        </AppRow>
      </Section>

      <Section>
        <SectionTitle>Tech Stack</SectionTitle>
        <TechList>
          {["Vue 3", "JavaScript", "Three.js", "Vue Router", "VueX", "JSON", "SCSS"].map((tech) => (
            <TechItem key={tech}>{tech}</TechItem>
          ))}
        </TechList>
      </Section>

      <Footer />
    </PageContainer>
  );
};

export default HyundaiModel;
